// The Maia access token minted by the broker. Only the fields the SPA reads are
// declared; `tenant_id` is the authoritative tenant the token is scoped to and
// `exp` (seconds since epoch) drives nothing here — session timing is tracked
// from the broker's `expires_in`, not the token's `exp`.
export interface MaiaTokenClaims {
  sub: string;
  tenant_id: string;
  exp: number;
  permissions: string[];
  roles: string[];
  sid: string;
}

function decodeSegment(segment: string): unknown {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

// Decode (not verify) the broker-minted access token. The broker and backends
// verify the signature; the SPA only needs the tenant claim to keep the
// `X-Tenant-Id` header from ever conflicting with the token.
export function decodeMaiaToken(token: string): MaiaTokenClaims | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;

  try {
    const claims = decodeSegment(segments[1]) as Partial<MaiaTokenClaims>;
    if (typeof claims.tenant_id !== 'string') return null;
    return claims as MaiaTokenClaims;
  } catch {
    return null;
  }
}

export function tenantIdOf(token: string): string | null {
  return decodeMaiaToken(token)?.tenant_id ?? null;
}
